import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, Filter, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { StatusPill, type DeploymentStatus } from "@/components/StatusPill";
import { EnvBadge, type Env } from "@/components/EnvBadge";
import { CommitRef } from "@/components/CommitRef";
import { RelativeTime } from "@/components/RelativeTime";
import { Avatar, AvatarFallback, identicon } from "@/components/ui/avatar";
import { RollbackButton } from "@/components/RollbackButton";
import { duration, shortSha } from "@/lib/format";

interface DeploymentRow {
  id: string;
  status: DeploymentStatus;
  env: Env;
  branch: string;
  commitSha: string;
  commitMessage?: string;
  authorEmail?: string;
  envResolvedFrom?: string;
  createdAt: string;
  readyAt?: string | null;
  durationMs?: number | null;
  isActiveForEnv?: boolean;
}

export function Deployments() {
  const { org, project } = useParams<{ org: string; project: string }>();

  const q = useQuery({
    queryKey: ["deployments", org, project],
    queryFn: async () => {
      const r = await api.get<{ deployments: DeploymentRow[] }>(
        `/orgs/${org}/projects/${project}/deployments?limit=50`,
      );
      return r.deployments;
    },
  });

  return (
    <div>
      <LiveNowCard orgSlug={org!} projectSlug={project!} deployments={q.data ?? []} />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          {q.data?.length ?? 0} deployments
        </div>
        <Button variant="default" size="sm"><Filter className="h-3.5 w-3.5" /> Filter</Button>
      </div>
      <div className="of-card overflow-hidden">
        {q.isLoading && <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading deployments…</div>}
        {q.data?.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">
            No deployments yet. Push a commit to trigger one.
          </div>
        )}
        {q.data?.map((d) => {
          const trigger = triggerLabel(d.envResolvedFrom, d.authorEmail);
          const ic = trigger.author ? identicon(trigger.author) : null;
          return (
            <Link
              key={d.id}
              to={`/orgs/${org}/projects/${project}/deployments/${d.id}`}
              className="of-row flex-wrap"
            >
              <StatusPill status={d.status} />
              <EnvBadge env={d.env} />
              <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">
                <CommitRef branch={d.branch} sha={d.commitSha} message={d.commitMessage ?? null} />
              </div>
              <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground" title={trigger.tooltip}>
                {ic ? (
                  <Avatar className="h-4 w-4">
                    <AvatarFallback className="text-[8px] text-white" style={{ background: ic.bg }}>
                      {ic.initials}
                    </AvatarFallback>
                  </Avatar>
                ) : (
                  <span className="text-2xs uppercase tracking-wider font-mono bg-subtle border border-border rounded px-1.5 py-0.5">
                    {trigger.label}
                  </span>
                )}
                {trigger.author && (
                  <span className="font-mono truncate max-w-[140px]">{trigger.author}</span>
                )}
              </div>
              <div className="hidden sm:block text-xs text-muted-foreground tabular-nums w-12 text-right">
                {duration(d.durationMs ?? null)}
              </div>
              <RelativeTime value={d.readyAt ?? d.createdAt} />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Resolve a human-readable trigger from envResolvedFrom (set by the
 * enqueue path) and the optional author email. Webhook-driven deploys
 * carry an author; manual ones don't, so we render a "manual"/"webhook"
 * badge instead of the misleading "unknown".
 */
function triggerLabel(
  envResolvedFrom: string | undefined,
  authorEmail: string | undefined,
): { label: string; author: string | null; tooltip: string } {
  if (authorEmail) {
    return { label: "git", author: authorEmail, tooltip: `pushed by ${authorEmail}` };
  }
  const from = envResolvedFrom ?? "";
  if (from.startsWith("manual:redeploy")) return { label: "redeploy", author: null, tooltip: from };
  if (from.startsWith("auto:project-create")) return { label: "initial", author: null, tooltip: "first deploy on project create" };
  if (from.startsWith("manual:")) return { label: "manual", author: null, tooltip: from };
  if (from.startsWith("deploy_hook:")) return { label: `hook(${from.slice("deploy_hook:".length)})`, author: null, tooltip: from };
  if (from.startsWith("pr:")) return { label: "pr", author: null, tooltip: from };
  if (from.startsWith("push:")) return { label: "push", author: null, tooltip: from };
  return { label: "system", author: null, tooltip: from || "no trigger info" };
}

/**
 * "Live now" — per (aliased) env, the currently-active deployment with a
 * rollback button to the most-recent prior `ready` deployment. Hidden when
 * no deployments are marked active (fresh project, or backfill never ran).
 */
function LiveNowCard({
  orgSlug,
  projectSlug,
  deployments,
}: {
  orgSlug: string;
  projectSlug: string;
  deployments: DeploymentRow[];
}) {
  const ALIASED_ENVS: Array<"production" | "staging" | "development"> = ["production", "staging", "development"];

  const activeByEnv = new Map<string, DeploymentRow>();
  for (const d of deployments) {
    if (d.isActiveForEnv && d.status === "ready") activeByEnv.set(d.env, d);
  }
  const visibleEnvs = ALIASED_ENVS.filter((e) => activeByEnv.has(e));
  if (visibleEnvs.length === 0) return null;

  // Per env, the previous ready deployment is the most-recent ready row
  // that's NOT the current active. Used as the rollback target.
  const priorByEnv = new Map<string, DeploymentRow | null>();
  for (const env of visibleEnvs) {
    const prior = deployments.find(
      (d) => d.env === env && d.status === "ready" && !d.isActiveForEnv,
    );
    priorByEnv.set(env, prior ?? null);
  }

  return (
    <div className="of-card mb-4">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Radio className="h-3.5 w-3.5 text-success" /> Live now
        </h3>
        <span className="text-2xs text-subtle-foreground">{visibleEnvs.length} active</span>
      </div>
      {visibleEnvs.map((env) => {
        const active = activeByEnv.get(env)!;
        const prior = priorByEnv.get(env) ?? null;
        return (
          <div key={env} className="of-row flex-wrap gap-3 items-center">
            <EnvBadge env={active.env} />
            <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:flex-1">
              <CommitRef branch={active.branch} sha={active.commitSha} message={active.commitMessage ?? null} />
            </div>
            <Link
              to={`/orgs/${orgSlug}/projects/${projectSlug}/deployments/${active.id}`}
              className="text-2xs font-mono text-accent hover:underline"
            >
              {shortSha(active.id, 8)}
            </Link>
            {prior ? (
              <RollbackButton
                orgSlug={orgSlug}
                projectSlug={projectSlug}
                env={env}
                targetDeploymentId={prior.id}
                label={`← ${shortSha(prior.commitSha)}`}
                size="sm"
                variant="default"
              />
            ) : (
              <span className="text-2xs text-subtle-foreground">no prior</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
